import { useEffect, useRef, type RefObject } from 'react';
import type { MotionValue } from 'framer-motion';
import { getTabs, getTabContent } from '@/components/ui/Cchatteaser/Cchatteaser.data';
import { CHATTEASER3_ANIM_PHASE } from './chatteaser3Anim';
import { measureChatTeaser3Layout } from './chatteaser3Layout';
import { useChatteaser3AnimationState } from './useChatteaser3AnimationState';
import { useChatteaser3LayoutState } from './useChatteaser3LayoutState';
import { useChatteaser3Intro } from './chatteaser3Intro';
import {
  CHATTEASER3_SCROLL_MODE,
  useChatteaser3Scroll,
} from './useChatteaser3Scroll';
import {
  type ChatTeaser3ScrollMotion,
  useChatteaser3ScrollMotion,
} from './useChatteaser3ScrollMotion';

type AnimPhaseLocal = (typeof CHATTEASER3_ANIM_PHASE)[keyof typeof CHATTEASER3_ANIM_PHASE];

const DEFAULT_ROTATION = 26;
const DEFAULT_DEPTH = 30;
const DEFAULT_SPREAD = 130;

interface Chatteaser3Runtime {
  activeTab: number;
  animPhase: AnimPhaseLocal;
  depth: number;
  displayedTab: number;
  handleSelectTab: (index: number) => void;
  intro: ReturnType<typeof useChatteaser3Intro>;
  layout: ReturnType<typeof measureChatTeaser3Layout>;
  layoutVersion: ReturnType<typeof useChatteaser3LayoutState>['layoutVersion'];
  maxScroll: number;
  messagesInnerRef: RefObject<HTMLDivElement | null>;
  railScrollProgress: number;
  rotation: number;
  scrollMotion: ChatTeaser3ScrollMotion;
  scrollY: MotionValue<number>;
  soloContentRef: RefObject<HTMLDivElement | null>;
  soloRef: RefObject<HTMLDivElement | null>;
  spread: number;
  tabContent: ReturnType<typeof getTabContent>;
  tabs: ReturnType<typeof getTabs>;
  unitedHeaderRef: RefObject<HTMLDivElement | null>;
  unitedScrollerRef: RefObject<HTMLDivElement | null>;
  wrapperRef: RefObject<HTMLDivElement | null>;
}

export function useChatteaser3Runtime(): Chatteaser3Runtime {
  const tabs = getTabs();
  const resetScrollRef = useRef<() => void>(() => {});
  const {
    activeTab,
    animPhase,
    displayedTab,
    handleSelectTab,
    railScrollProgress,
  } = useChatteaser3AnimationState({
    resetScroll: () => resetScrollRef.current(),
    tabCount: tabs.length,
  });
  const {
    layout,
    layoutRef,
    layoutVersion,
    maxScroll,
    messagesInnerRef,
    soloContentRef,
    soloRef,
    unitedHeaderRef,
    unitedScrollerRef,
    wrapperRef,
  } = useChatteaser3LayoutState({
    displayedTab,
  });
  const { resetScroll, scrollY } = useChatteaser3Scroll({
    maxScroll,
    mode: CHATTEASER3_SCROLL_MODE.Wheel,
    wrapperRef,
  });
  useEffect(() => {
    resetScrollRef.current = resetScroll;
  }, [resetScroll]);
  const scrollMotion = useChatteaser3ScrollMotion({
    layoutRef,
    layoutVersion,
    scrollY,
  });
  const intro = useChatteaser3Intro();
  const tabContent = getTabContent(displayedTab);

  return {
    activeTab,
    animPhase,
    depth: DEFAULT_DEPTH,
    displayedTab,
    handleSelectTab,
    intro,
    layout,
    layoutVersion,
    maxScroll,
    messagesInnerRef,
    railScrollProgress,
    rotation: DEFAULT_ROTATION,
    scrollMotion,
    scrollY,
    soloContentRef,
    soloRef,
    spread: DEFAULT_SPREAD,
    tabContent,
    tabs,
    unitedHeaderRef,
    unitedScrollerRef,
    wrapperRef,
  };
}
